import React, { createContext, useContext, useCallback, useRef, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';

// Shared, uniform confirmation dialog to replace blocking window.confirm() calls
// for destructive actions. Usage:
//   const confirm = useConfirm();
//   if (!(await confirm({ title: 'Delete user', message: '...', confirmText: 'Delete' }))) return;
const ConfirmContext = createContext(() => Promise.resolve(false));

export const useConfirm = () => useContext(ConfirmContext);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState({
    open: false,
    title: 'Please confirm',
    message: 'Are you sure?',
    confirmText: 'Confirm',
    cancelText: 'Cancel'
  });
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    setState({
      open: true,
      title: options.title || 'Please confirm',
      message: options.message || 'Are you sure?',
      confirmText: options.confirmText || 'Confirm',
      cancelText: options.cancelText || 'Cancel'
    });
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const close = (result) => {
    setState((s) => ({ ...s, open: false }));
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={state.open} onClose={() => close(false)} aria-labelledby="confirm-dialog-title">
        <DialogTitle id="confirm-dialog-title">{state.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{state.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => close(false)}>{state.cancelText}</Button>
          <Button onClick={() => close(true)} color="secondary" variant="contained" autoFocus>
            {state.confirmText}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmContext.Provider>
  );
}
